import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, SESSION_ENDED_EVENT } from './api';
import { deadlineFrom } from './sessionClock';
import type { Session, Theme, TwoFactorChallenge } from './types';

interface SessionValue {
  session: Session | null;
  loading: boolean;
  /** Set when the session call itself failed, as opposed to simply not being signed in. */
  error: string | null;
  /**
   * Why a session that existed a moment ago no longer does — signed in elsewhere, password
   * changed, account deactivated. Distinct from `error`, which is the API being unreachable,
   * and from the ordinary not-signed-in case, which has no reason to give.
   */
  endedReason: string | null;
  /** Resolves to a challenge when a second step is owed, or null when already signed in. */
  signIn: (email: string, password: string) => Promise<TwoFactorChallenge | null>;
  completeTwoFactor: (challengeId: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  theme: Theme;
  resolvedTheme: 'dark' | 'light';
  setTheme: (theme: Theme) => void;
  /** Records the company this person is working in, for the pages that open on one. */
  rememberCompany: (companyId: string) => void;
}

const SessionContext = createContext<SessionValue | null>(null);
const THEME_KEY = 'hub.theme';

/** What the page says when the twelve hours are up — the reason most people will meet. */
const EXPIRED = 'You were signed out automatically: a session lasts 12 hours. Sign in again to carry on.';
/** How often the countdown is checked. Coarse on purpose — see the expiry effect below. */
const EXPIRY_CHECK_MS = 15_000;
/** Shared by every open tab of the Hub in this browser, so signing out of one signs out all. */
const CHANNEL = 'hub-session';

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {
    /* private browsing, fall through */
  }
  // Light unless someone has chosen otherwise, matching the pre-paint script in index.html.
  return 'light';
}

function systemTheme(): 'dark' | 'light' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemPref, setSystemPref] = useState<'dark' | 'light'>(systemTheme);

  // Follow the OS while the choice is "system".
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemPref(query.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = theme === 'system' ? systemPref : theme;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
  }, [resolvedTheme]);

  const refresh = useCallback(async () => {
    try {
      const next = await api.get<Session>('/auth/session');
      setSession(next);
      // The server-side preference wins on sign-in, so the choice follows the person
      // between browsers rather than living only in this one.
      if (next.person.theme && next.person.theme !== readStoredTheme()) {
        setThemeState(next.person.theme);
        try {
          localStorage.setItem(THEME_KEY, next.person.theme);
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      setSession(null);
      // 401 is the ordinary "not signed in" path. Anything else is a fault worth showing:
      // rethrowing here used to surface as an unhandled rejection and a blank page.
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        /**
         * BUT NOT EVERY 401 IS "YOU WERE NEVER SIGNED IN".
         *
         * Being turned out because the account signed in somewhere else, or because the
         * password changed, arrives here as a 401 like any other — and landing on a blank
         * login screen with no word about it reads as the Hub having dropped you. The API says
         * which it was; the only one with nothing to explain is not having a cookie at all.
         */
        setEndedReason(err.message === 'Not signed in' ? null : err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not reach the Hub API.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
    The session as it stands, for listeners that outlive a render. An event handler that read
    `session` from its closure would see whatever it was when the handler was attached.
  */
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  /*
    Where the twelve hours run out, on THIS machine's clock. The server says when the session
    ends and what time it was when it said so; the gap between the two is what is left, and
    counting that down locally makes the sign-out land on time even if this computer's clock is
    wrong. Comparing the server's `expiresAt` to `Date.now()` directly would sign somebody out
    early, or late, by however far their clock has drifted.
  */
  const deadline = useRef<number | null>(null);
  useEffect(() => {
    deadline.current = deadlineFrom(session?.expiresAt, session?.serverNow, Date.now());
  }, [session?.expiresAt, session?.serverNow]);

  /** One way out, so every path that ends a session leaves the app in the same state. */
  const endSession = useCallback((reason: string | null, tellOtherTabs: boolean) => {
    if (!sessionRef.current) return;
    setSession(null);
    setEndedReason(reason);
    if (tellOtherTabs) channel.current?.postMessage({ type: 'signed-out', reason });
  }, []);

  /*
    OTHER TABS FOLLOW. Signing out of one tab used to leave every other open tab showing the
    Hub — fully rendered, until its next click failed. A BroadcastChannel reaches every tab of
    this origin in this browser at once. Absent in very old browsers, where the 401 handling
    below still catches it on the next click.
  */
  const channel = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(CHANNEL);
    channel.current = bc;
    bc.onmessage = (event: MessageEvent<{ type?: string; reason?: string | null }>) => {
      if (event.data?.type === 'signed-out') {
        endSession(event.data.reason ?? 'You signed out in another tab.', false);
      }
    };
    return () => {
      bc.close();
      channel.current = null;
    };
  }, [endSession]);

  /*
    ANY 401 WHILE SIGNED IN ENDS THE SESSION HERE TOO — see `SESSION_ENDED_EVENT` in api.ts for
    why that is safe. The guard's own message comes through as the reason, so "signed in on
    another device" and "your password changed" still say what happened.
  */
  useEffect(() => {
    const onEnded = (event: Event) => {
      const message = (event as CustomEvent<string>).detail;
      endSession(message === 'Not signed in' ? null : message, true);
    };
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, [endSession]);

  /**
   * THE TWELVE-HOUR SIGN-OUT, on time and with nothing clicked.
   *
   * The server has always refused the cookie after twelve hours. What it could not do was take
   * the page away: somebody who left the Hub open overnight came back to a fully drawn screen
   * that failed on its first click. Now the app signs itself out when the time is up.
   *
   * CHECKED ON AN INTERVAL, NOT ONE LONG TIMER. A single `setTimeout` for twelve hours does not
   * fire on time on a laptop that slept through it — timers pause with the machine. Checking
   * every few seconds against the deadline, and again the moment the tab is looked at, means a
   * lid opened the next morning shows the login page rather than yesterday's board.
   *
   * And coming back to the tab ASKS the server, at most once every half minute. That is how a
   * tab that sat idle notices it was signed in elsewhere, or that the account was deactivated,
   * without waiting for a click. A success changes nothing — it is deliberately not a refresh,
   * which would rebuild the session object and knock every page holding state (ticked rows,
   * half-typed edits) back to where it started.
   */
  const lastAsked = useRef(0);
  useEffect(() => {
    if (!session) return;

    const check = () => {
      if (deadline.current !== null && Date.now() >= deadline.current) {
        endSession(EXPIRED, true);
      }
    };
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      check();
      if (sessionRef.current && Date.now() - lastAsked.current > 30_000) {
        lastAsked.current = Date.now();
        /* A 401 here is handled by the event above; anything else is not this code's business. */
        api.get('/auth/session').catch(() => undefined);
      }
    };

    const timer = window.setInterval(check, EXPIRY_CHECK_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    check();
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [session, endSession]);

  /**
   * Signing in, which is now sometimes two calls.
   *
   * Returns the challenge when a second step is owed instead of loading a session, so the
   * login page can ask for the code. Nothing is signed in at that point — the cookie only
   * arrives after `completeTwoFactor`.
   */
  const signIn = useCallback(
    async (email: string, password: string): Promise<TwoFactorChallenge | null> => {
      const result = await api.post<{ ok?: true } | TwoFactorChallenge>('/auth/login', { email, password });
      if (result && 'twoFactorRequired' in result && result.twoFactorRequired) return result;

      setLoading(true);
      setError(null);
      /* They have signed in; whatever ended the last session is answered and stops being news. */
      setEndedReason(null);
      await refresh();
      return null;
    },
    [refresh],
  );

  const completeTwoFactor = useCallback(
    async (challengeId: string, code: string) => {
      await api.post('/auth/two-factor', { challengeId, code });
      setLoading(true);
      setError(null);
      setEndedReason(null);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setSession(null);
      /* Leaving on purpose needs no explanation on the way out. */
      setEndedReason(null);
      /* But the other open tabs do — they did not press anything. */
      channel.current?.postMessage({ type: 'signed-out', reason: 'You signed out in another tab.' });
    }
  }, []);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        /* ignore */
      }
      // Persisted quietly; a failure here must not undo what the user just clicked.
      if (session) void api.patch('/me/preferences', { theme: next }).catch(() => {});
    },
    [session],
  );

  /**
   * Remembering the company, in the session as well as on the server.
   *
   * Writing only to the server was the bug: the pages read the remembered company from the
   * session they are holding, and that copy was still the old one until something refetched
   * it — so choosing a company on one page did nothing on the next until a hard refresh.
   *
   * Updating the local copy first is what makes it take effect immediately, and it is the
   * same shape as `setTheme` above for the same reason: the user has already chosen, the
   * screen should already agree, and the write is a background detail. A failed write leaves
   * the choice working for this session and forgotten by the next, which is the right way for
   * a convenience to fail.
   */
  const rememberCompany = useCallback((companyId: string) => {
    if (!companyId) return;
    setSession((current) =>
      current && current.person.lastCompanyId !== companyId
        ? { ...current, person: { ...current.person, lastCompanyId: companyId } }
        : current,
    );
    void api.patch('/me/preferences', { lastCompanyId: companyId }).catch(() => {});
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ session, loading, error, endedReason, signIn, completeTwoFactor, signOut, refresh, theme, resolvedTheme, setTheme, rememberCompany }),
    [session, loading, error, endedReason, signIn, completeTwoFactor, signOut, refresh, theme, resolvedTheme, setTheme, rememberCompany],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}

/** The signed-in session, for routes that only render behind the auth gate. */
/**
 * Where someone lands after signing in. Belong to exactly one company and you go straight to
 * its dashboard — a customer with one company should never see a list of one thing. Belong to
 * several, or to none as an administrator, and the companies list is the right start.
 */
export function landingPath(session: Session): string {
  if (session.companies.length === 1) return `/companies/${session.companies[0].id}`;
  return '/companies';
}

export function useAuthed(): Session {
  const { session } = useSession();
  if (!session) throw new Error('useAuthed used outside an authenticated route');
  return session;
}
